import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { employeeService } from '../../services/employeeService';
import { adminService } from '../../services/adminService';
import { Package, Calendar, Truck, CheckCircle, Clock, Filter, Search, Phone, Mail, RotateCcw, Check, X } from 'lucide-react';

export default function BarrelCustomerManagementDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'deposits', 'planning'
  
  // Data states
  const [barrelOrders, setBarrelOrders] = useState([]);
  const [barrelDeposits, setBarrelDeposits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Filter states
  const [orderFilters, setOrderFilters] = useState({
    status: 'all',
    searchTerm: ''
  });
  
  const [depositFilters, setDepositFilters] = useState({
    collectionStatus: 'all',
    plannedDate: '',
    assignedDriver: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [ordersData, depositsData, employeesData, statsData] = await Promise.all([
        barrelService?.getBarrelOrders(),
        barrelService?.getBarrelDeposits(),
        employeeService?.getAll({ isActive: true }),
        barrelService?.getBarrelStatistics()
      ]);
      
      setBarrelOrders(ordersData);
      setBarrelDeposits(depositsData);
      setEmployees(employeesData?.filter(e => e?.role === 'chauffeur'));
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await barrelService?.updateBarrelOrder(orderId, { status: newStatus });
      await loadData();
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUpdateDepositStatus = async (depositId, newStatus) => {
    try {
      await barrelService?.updateBarrelDeposit(depositId, { collection_status: newStatus });
      await loadData();
    } catch (err) {
      console.error('Error updating deposit status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleAssignDriver = async (depositId, driverId) => {
    try {
      await barrelService?.updateBarrelDeposit(depositId, { 
        assigned_driver_id: driverId,
        collection_status: 'scheduled'
      });
      await loadData();
    } catch (err) {
      console.error('Error assigning driver:', err);
      alert('Erreur lors de l\'assignation du chauffeur');
    }
  };

  const handleReactivateDeposit = async (depositId) => {
    try {
      const success = await barrelService?.reactivateBarrelDeposit(depositId);
      if (success) {
        await loadData();
        alert('Livraison réactivée avec succès');
      } else {
        alert('Impossible de réactiver cette livraison');
      }
    } catch (err) {
      console.error('Error reactivating deposit:', err);
      alert('Erreur lors de la réactivation de la livraison');
    }
  };

  const handleToggleCollectedLater = async (clientId, currentValue) => {
    try {
      await adminService?.updateClient(clientId, { 
        collected_later: !currentValue 
      });
      await loadData();
    } catch (err) {
      console.error('Error updating collected_later:', err);
      alert('Erreur lors de la mise à jour');
    }
  };

  const getBarrelTypeLabel = (type) => {
    const labels = {
      'noir_270l': 'Barrique Noire 270L',
      'bleu_220l': 'Barrique Bleue 220L'
    };
    return labels?.[type] || type;
  };

  const getStatusBadge = (status, type = 'order') => {
    const orderStatuses = {
      'pending': { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      'confirmed': { label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
      'delivered': { label: 'Livrée', color: 'bg-green-100 text-green-800' },
      'filled': { label: 'Remplie', color: 'bg-purple-100 text-purple-800' },
      'collected': { label: 'Collectée', color: 'bg-indigo-100 text-indigo-800' },
      'cancelled': { label: 'Annulée', color: 'bg-red-100 text-red-800' }
    };
    
    const collectionStatuses = {
      'pending': { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      'scheduled': { label: 'Planifiée', color: 'bg-blue-100 text-blue-800' },
      'in_progress': { label: 'En cours', color: 'bg-orange-100 text-orange-800' },
      'collected': { label: 'Collectée', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Annulée', color: 'bg-red-100 text-red-800' }
    };
    
    const statuses = type === 'order' ? orderStatuses : collectionStatuses;
    const statusInfo = statuses?.[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo?.color}`}>
        {statusInfo?.label}
      </span>
    );
  };

  const filteredOrders = barrelOrders?.filter(order => {
    if (orderFilters?.status !== 'all' && order?.status !== orderFilters?.status) return false;
    if (orderFilters?.searchTerm) {
      const search = orderFilters?.searchTerm?.toLowerCase();
      return (
        order?.customer_name?.toLowerCase()?.includes(search) ||
        order?.customer_phone?.toLowerCase()?.includes(search) ||
        order?.customer_email?.toLowerCase()?.includes(search)
      );
    }
    return true;
  });

  const filteredDeposits = barrelDeposits?.filter(deposit => {
    if (depositFilters?.collectionStatus !== 'all' && deposit?.collection_status !== depositFilters?.collectionStatus) return false;
    if (depositFilters?.assignedDriver !== 'all' && deposit?.assigned_driver_id !== depositFilters?.assignedDriver) return false;
    if (depositFilters?.plannedDate && deposit?.planned_collection_date !== depositFilters?.plannedDate) return false;
    if (depositFilters?.searchTerm) {
      const search = depositFilters?.searchTerm?.toLowerCase();
      return (
        deposit?.customer_name?.toLowerCase()?.includes(search) ||
        deposit?.customer_phone?.toLowerCase()?.includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données barriques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Barriques</h1>
          <p className="text-gray-600">Suivi complet des ventes de barriques vides, dépôts clients et planning de collecte</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Commandes Totales</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.totalOrders}</p>
                </div>
                <Package className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Revenu Total</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.totalRevenue?.toFixed(2)}€</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Dépôts Actifs</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.totalDeposits}</p>
                </div>
                <Clock className="w-10 h-10 text-orange-600" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Collectes Planifiées</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.scheduledCollections}</p>
                </div>
                <Truck className="w-10 h-10 text-purple-600" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'orders' ?'border-green-600 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Package className="w-5 h-5 inline mr-2" />
                Commandes de Barriques
              </button>
              <button
                onClick={() => setActiveTab('deposits')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'deposits' ?'border-green-600 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calendar className="w-5 h-5 inline mr-2" />
                Dépôts & Planning Collecte
              </button>
            </nav>
          </div>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-lg shadow-sm">
            {/* Filters */}
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Filter className="w-4 h-4 inline mr-1" />
                    Statut
                  </label>
                  <select
                    value={orderFilters?.status}
                    onChange={(e) => setOrderFilters({ ...orderFilters, status: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmée</option>
                    <option value="delivered">Livrée</option>
                    <option value="filled">Remplie</option>
                    <option value="collected">Collectée</option>
                    <option value="cancelled">Annulée</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Search className="w-4 h-4 inline mr-1" />
                    Rechercher
                  </label>
                  <input
                    type="text"
                    placeholder="Nom, téléphone, email..."
                    value={orderFilters?.searchTerm}
                    onChange={(e) => setOrderFilters({ ...orderFilters, searchTerm: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Orders Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type & Quantité</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Livraison</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders?.map((order) => (
                    <tr key={order?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order?.customer_name}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {order?.customer_phone}
                          </div>
                          {order?.customer_email && (
                            <div className="text-sm text-gray-500 flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {order?.customer_email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{getBarrelTypeLabel(order?.barrel_type)}</div>
                        <div className="text-sm text-gray-500">Quantité: {order?.quantity}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{order?.total_price}€</div>
                        <div className="text-sm text-gray-500">{order?.unit_price}€/unité</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {order?.delivery_date ? new Date(order?.delivery_date)?.toLocaleDateString('fr-FR') : 'Non planifiée'}
                        </div>
                        <div className="text-sm text-gray-500">{order?.customer_city}</div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(order?.status, 'order')}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={order?.status}
                          onChange={(e) => handleUpdateOrderStatus(order?.id, e?.target?.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="pending">En attente</option>
                          <option value="confirmed">Confirmée</option>
                          <option value="delivered">Livrée</option>
                          <option value="filled">Remplie</option>
                          <option value="collected">Collectée</option>
                          <option value="cancelled">Annulée</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredOrders?.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune commande trouvée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <div className="bg-white rounded-lg shadow-sm">
            {/* Filters */}
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut Collecte</label>
                  <select
                    value={depositFilters?.collectionStatus}
                    onChange={(e) => setDepositFilters({ ...depositFilters, collectionStatus: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="all">Tous</option>
                    <option value="pending">En attente</option>
                    <option value="scheduled">Planifiée</option>
                    <option value="in_progress">En cours</option>
                    <option value="collected">Collectée</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur</label>
                  <select
                    value={depositFilters?.assignedDriver}
                    onChange={(e) => setDepositFilters({ ...depositFilters, assignedDriver: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="all">Tous</option>
                    {employees?.map(emp => (
                      <option key={emp?.id} value={emp?.id}>{emp?.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Planifiée</label>
                  <input
                    type="date"
                    value={depositFilters?.plannedDate}
                    onChange={(e) => setDepositFilters({ ...depositFilters, plannedDate: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher</label>
                  <input
                    type="text"
                    placeholder="Nom, téléphone..."
                    value={depositFilters?.searchTerm}
                    onChange={(e) => setDepositFilters({ ...depositFilters, searchTerm: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Deposits Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barriques</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dépôt</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collecte Prévue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chauffeur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collecté plus tard</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeposits?.map((deposit) => (
                    <tr key={deposit?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{deposit?.customer_name}</div>
                          <div className="text-sm text-gray-500">{deposit?.customer_phone}</div>
                          <div className="text-sm text-gray-500">{deposit?.customer_city}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{getBarrelTypeLabel(deposit?.barrel_type)}</div>
                        <div className="text-sm text-gray-500">Qté: {deposit?.quantity}</div>
                        <div className="text-sm text-gray-500">{deposit?.filling_status}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {new Date(deposit?.deposit_date)?.toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {deposit?.planned_collection_date 
                            ? new Date(deposit?.planned_collection_date)?.toLocaleDateString('fr-FR')
                            : 'Non planifiée'
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={deposit?.assigned_driver_id || ''}
                          onChange={(e) => handleAssignDriver(deposit?.id, e?.target?.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Non assigné</option>
                          {employees?.map(emp => (
                            <option key={emp?.id} value={emp?.id}>{emp?.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleCollectedLater(deposit?.client_id, deposit?.clients?.collected_later)}
                          className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            deposit?.clients?.collected_later
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          disabled={!deposit?.client_id}
                        >
                          {deposit?.clients?.collected_later ? (
                            <>
                              <Check className="w-3 h-3" />
                              Oui
                            </>
                          ) : (
                            <>
                              <X className="w-3 h-3" />
                              Non
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(deposit?.collection_status, 'collection')}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={deposit?.collection_status}
                          onChange={(e) => handleUpdateDepositStatus(deposit?.id, e?.target?.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="pending">En attente</option>
                          <option value="scheduled">Planifiée</option>
                          <option value="in_progress">En cours</option>
                          <option value="collected">Collectée</option>
                          <option value="cancelled">Annulée</option>
                        </select>
                        {deposit?.collection_status === 'cancelled' && (
                          <button
                            onClick={() => handleReactivateDeposit(deposit?.id)}
                            className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Réactiver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredDeposits?.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun dépôt trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}